import React, { useState, useEffect, useContext } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import TreeView from "react-treeview";
/* para que salga el logueo */
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
/* para que salga el logueo */
import {
  Box,
  CircularProgress,
  Typography,
  TextField,
  Tooltip,
  IconButton,
  Paper,
} from "@mui/material";
import {
  AccountTreeOutlined,
  Business,
  Add,
  ImportExport,
} from "@mui/icons-material";

import {
  getProveedoresConTipo,
  getProveedorTipos,
  updateProveedor,
} from "../../api/ProveedoresyFabricas/proveedoresApi";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import ProveedoresFicha from "./ProveedoresFicha";
import ProveedoresFabricas from "./ProveedoresFabricas";

import ProveedoresFichaDrawer from "./ProveedoresFichaDrawer";

import ProveedoresContacts from "./ProveedoresContacts";
import ProveedoresTraspasoFabricaDialog from "./ProveedoresTraspasoFabricaDialog";

const Proveedores = () => {
  const theme = useTheme();
  const { t } = useTranslation();

  const navigate = useNavigate();
  /*   const { isMinimized } = useContext(SidebarContext);
  const marginLeft = isMinimized ? "4em" : "15em"; */

  const { contentMargin } = useContext(SidebarContext);

  const [proveedores, setProveedores] = useState([]);
  const [tiposProveedor, setTiposProveedor] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedProveedor, setSelectedProveedor] = useState(null);

  const [selectedProveedorId, setSelectedProveedorId] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [traspasoDialogOpen, setTraspasoDialogOpen] = useState(false);

  const filterInputSx = {
    mt: 2,
    width: "100%",
    "& .MuiInputBase-root": {
      backgroundColor: theme.palette.caja.main,
      borderRadius: 2,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
    },
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
  };

  const handleEditProveedor = () => {
    setDrawerOpen(true);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
    } else {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decodedToken.exp < currentTime) {
          localStorage.removeItem("token");
          navigate("/login");
        }
      } catch (error) {
        console.error("Error decoding token:", error);
        navigate("/login");
      }
    }

    const fetchData = async () => {
      try {
        const [proveedoresData, tiposProveedorData] = await Promise.all([
          getProveedoresConTipo(),
          getProveedorTipos(),
        ]);
        setProveedores(proveedoresData);
        setTiposProveedor(tiposProveedorData);
      } catch (error) {
        console.error("Error al obtener datos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // ✅ Agrupar los proveedores correctamente
  const groupByHierarchy = (proveedores, tipos) => {
    const grouped = (tipos || []).reduce((acc, tipo) => {
      const nombreTipo = tipo?.ProveedorTipo?.trim();
      if (nombreTipo && !acc[nombreTipo]) {
        acc[nombreTipo] = {};
      }
      return acc;
    }, {});

    return proveedores.reduce((acc, proveedor) => {
      const tipo = proveedor.tipoProveedor?.ProveedorTipo || "WITHOUT TYPE";
      const pais = proveedor.PAIS || "WITHOUT COUNTRY";

      if (!acc[tipo]) {
        acc[tipo] = {};
      }
      if (!acc[tipo][pais]) {
        acc[tipo][pais] = [];
      }
      acc[tipo][pais].push(proveedor);

      return acc;
    }, grouped);
  };

  /** Orden alfabético: tipos → países → nombre de proveedor */
  const sortGroupedHierarchy = (data) => {
    if (!data || typeof data !== "object") return data;
    const sorted = {};
    Object.keys(data)
      .sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base", numeric: true }),
      )
      .forEach((tipo) => {
        sorted[tipo] = {};
        Object.keys(data[tipo])
          .sort((a, b) =>
            a.localeCompare(b, undefined, {
              sensitivity: "base",
              numeric: true,
            }),
          )
          .forEach((pais) => {
            const list = data[tipo][pais];
            sorted[tipo][pais] = [...list].sort((p1, p2) =>
              (p1.N_PROVEEDOR || "").localeCompare(
                p2.N_PROVEEDOR || "",
                undefined,
                { sensitivity: "base", numeric: true },
              ),
            );
          });
      });
    return sorted;
  };

  const groupedData = sortGroupedHierarchy(
    groupByHierarchy(proveedores, tiposProveedor),
  );

  // ✅ Manejo de la expansión de nodos
  const toggleNode = (key) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // ✅ Filtrado de búsqueda
  const filterData = (data, searchTerm) => {
    if (!searchTerm) return data;

    const filtered = {};
    const normalizedSearch = searchTerm.toLowerCase();
    Object.keys(data).forEach((tipo) => {
      const filteredPaises = {};
      Object.keys(data[tipo]).forEach((pais) => {
        const filteredProveedores = data[tipo][pais].filter((proveedor) =>
          `${proveedor.N_PROVEEDOR} ${tipo} ${pais}`
            .toLowerCase()
            .includes(normalizedSearch),
        );

        if (filteredProveedores.length > 0) {
          filteredPaises[pais] = filteredProveedores;
        }
      });

      if (
        Object.keys(filteredPaises).length > 0 ||
        tipo.toLowerCase().includes(normalizedSearch)
      ) {
        filtered[tipo] = filteredPaises;
      }
    });

    return filtered;
  };

  const filteredData = sortGroupedHierarchy(
    filterData(groupedData, searchTerm),
  );

  const handleSelectProveedor = (proveedor) => {
    setSelectedProveedor(proveedor);
    setSelectedProveedorId(proveedor.CODPROV); // ✅ Guardamos el ID seleccionado
  };

  const handleSaveProveedor = async (updatedProveedor) => {
    try {
      // ✅ Actualizar en el backend
      await updateProveedor(updatedProveedor);

      // ✅ Volver a cargar proveedores desde la API
      const updatedProveedores = await getProveedoresConTipo();
      setProveedores(updatedProveedores);

      // ✅ Buscar y actualizar el proveedor seleccionado
      const proveedorActualizado = updatedProveedores.find(
        (prov) => prov.CODPROV === updatedProveedor.CODPROV,
      );
      setSelectedProveedor(proveedorActualizado);
    } catch (error) {
      console.error("Error al actualizar proveedor:", error);
    }
  };

  // ✅ Renderización del árbol
  const renderTreeItems = () => {
    return Object.keys(filteredData).map((tipo) => (
      <Box
        key={tipo}
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          padding: "1em 0.5em",
          marginBottom: 0.5,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[1],
        }}
      >
        <TreeView
          nodeLabel={
            <div
              style={{
                /*  width: "450px", */
                display: "flex",
                alignItems: "center",
                padding: "0.5em",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: ".9em",
              }}
              onClick={() => toggleNode(tipo)}
            >
              <AccountTreeOutlined
                sx={{ color: "text.secondary", marginRight: ".5em" }}
              />
              {tipo}
            </div>
          }
          collapsed={!expandedNodes[tipo]}
        >
          {Object.keys(filteredData[tipo]).length === 0 ? (
            <Box
              sx={{
                px: 2,
                py: 1,
                color: "text.secondary",
                fontSize: ".8em",
              }}
            >
              {t("ProveedoresPage.noSuppliersForType", {
                defaultValue: "Sin proveedores",
              })}
            </Box>
          ) : (
            Object.keys(filteredData[tipo]).map((pais) => (
              <TreeView
                key={pais}
                nodeLabel={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontWeight: 600,
                      cursor: "pointer",
                      padding: "0.5em",
                      fontSize: ".8em",
                    }}
                    onClick={() => toggleNode(`${tipo}-${pais}`)}
                  >
                    🌍 {pais}
                  </div>
                }
                collapsed={!expandedNodes[`${tipo}-${pais}`]}
              >
                {filteredData[tipo][pais].map((proveedor) => (
                  <div
                    key={proveedor.CODPROV}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      padding: ".5em",
                      backgroundColor:
                        selectedProveedorId === proveedor.CODPROV
                          ? alpha(theme.palette.primary.main, 0.1)
                          : "transparent",
                      borderRadius: "4px",
                      fontSize: ".8em",
                    }}
                    onClick={() => handleSelectProveedor(proveedor)}
                  >
                    <Business
                      sx={{ color: "primary.main", marginRight: ".5em" }}
                    />
                    {proveedor.N_PROVEEDOR}
                  </div>
                ))}
              </TreeView>
            ))
          )}
        </TreeView>
      </Box>
    ));
  };

  const shellPad = 24;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        flexWrap: { xs: "wrap", lg: "nowrap" },
        alignItems: { xs: "stretch", lg: "stretch" },
        boxSizing: "border-box",
        minHeight: { xs: "min-content", lg: `calc(100dvh - ${shellPad}px)` },
        maxHeight: { lg: `calc(100dvh - ${shellPad}px)` },
        overflow: { xs: "visible", lg: "hidden" },
        overflowY: { xs: "auto", lg: "hidden" },
        pt: 2,
        pr: { xs: 1.5, md: 3 },
        pb: 2,
        pl: 2,
        gap: 2,
        ...contentMargin,
      }}
    >
      <Box
        sx={{
          minWidth: { xs: "100%", sm: 400 },
          maxWidth: 440,
          width: { xs: "100%", sm: "auto" },
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          maxHeight: { lg: `calc(100dvh - ${shellPad}px)` },
          overflowY: { lg: "auto" },
          overflowX: "hidden",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: 3,
            border: 1,
            borderColor: "divider",
            position: "sticky",
            top: 0,
            zIndex: 9,
            background:
              "linear-gradient(135deg, rgba(0,188,212,0.12) 0%, rgba(103,58,183,0.1) 100%)",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 2px 12px rgba(0,0,0,0.35)"
                : "0 2px 12px rgba(0,0,0,0.06)",
            mb: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 1, display: "block" }}
                  >
                    CRM
                  </Typography>
                  <Typography
                    component="h1"
                    variant="h5"
                    sx={{ fontWeight: 800, lineHeight: 1.2 }}
                  >
                    {t("sidebar.suppliers")}
                  </Typography>
                </Box>{" "}
                <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title={t("ProveedoresPage.transferToMillsTooltip")}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => setTraspasoDialogOpen(true)}
                      sx={{
                        border: 1,
                        borderColor: "primary.main",
                      }}
                    >
                      <ImportExport />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("ProveedoresPage.addContact")}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => {
                        setSelectedProveedor(null);
                        setDrawerOpen(true);
                      }}
                      sx={{
                        border: 1,
                        borderColor: "primary.main",
                      }}
                    >
                      <Add />
                    </IconButton>
                  </Tooltip>
                </Box>{" "}
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5, maxWidth: 400 }}
              >
                {t("ProveedoresPage.heroSubtitle")}
              </Typography>
            </Box>
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t("ProveedoresPage.searchPlaceholder")}
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputProps={{
              "aria-label": t("ProveedoresPage.searchPlaceholder"),
            }}
            sx={filterInputSx}
          />
        </Paper>

        {loading ? (
          <CircularProgress sx={{ display: "block", my: 2 }} />
        ) : (
          renderTreeItems()
        )}
      </Box>
      {selectedProveedor && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            height: { lg: `calc(100dvh - ${shellPad}px)` },
            maxHeight: { lg: `calc(100dvh - ${shellPad}px)` },
            overflow: "hidden",
            gap: 2,
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              flex: { lg: "1 1 0" },
              minWidth: { lg: 0 },
              maxWidth: { lg: 440 },
              display: "flex",
              minHeight: 0,
            }}
          >
            <ProveedoresFicha
              proveedor={selectedProveedor}
              onSave={handleSaveProveedor}
              onClose={() => setSelectedProveedor(null)}
              onEdit={handleEditProveedor}
            />
          </Box>
          <Box
            sx={{
              flex: { lg: "1 1 0" },
              minWidth: { lg: 0 },
              maxWidth: { lg: 440 },
              display: "flex",
              minHeight: 0,
            }}
          >
            <ProveedoresContacts proveedor={selectedProveedor} />
          </Box>
          {selectedProveedor.tipoProveedor?.ProveedorTipo === "TRADER" && (
            <Box
              sx={{
                flex: { lg: "1 1 0" },
                minWidth: { lg: 0 },
                maxWidth: { lg: 440 },
                display: "flex",
                minHeight: 0,
              }}
            >
              <ProveedoresFabricas proveedor={selectedProveedor} />
            </Box>
          )}
        </Box>
      )}
      <ProveedoresFichaDrawer
        proveedor={selectedProveedor}
        open={isDrawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSaveProveedor}
      />
      <ProveedoresTraspasoFabricaDialog
        open={traspasoDialogOpen}
        onClose={() => setTraspasoDialogOpen(false)}
        onComplete={async (result) => {
          const list = await getProveedoresConTipo();
          setProveedores(list);
          if (result?.proveedorEliminado) {
            setSelectedProveedor(null);
            setSelectedProveedorId(null);
          } else if (selectedProveedorId != null) {
            const still = list.find((p) => p.CODPROV === selectedProveedorId);
            setSelectedProveedor(still || null);
            if (!still) setSelectedProveedorId(null);
          }
        }}
      />
    </Box>
  );
};

export default Proveedores;
